import { produce } from 'immer'

import { DeepAgentState } from './deep-agent.state'

import { DeepAgent, FeedbackType, Message, MessageType } from '../types/deep-agent.dictionary'

const onGetAgentResponseStream = (
  state: DeepAgentState,
  { response }: { response: any }
): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.streamingAiResponse = true
    draft.pendingAiResponse = true

    const lastMessageDraft = draft.messages[draft.messages.length - 1]
    const isLastMessageOfTypeAi =
      lastMessageDraft &&
      lastMessageDraft?.type === MessageType.AiMsg &&
      !lastMessageDraft?.commited
    const { content, reasoning, streaming, chunk, type, error, pass_to_human } = response
    if (error) {
      return
    } else if (type === 'patch_tool_calls') {
      draft.pendingRequestActionMessage = 'Patching tool calls.'
    } else if (type === 'summarization') {
      draft.pendingRequestActionMessage = 'Summarizing.'
    } else if (type === 'tool_call_chunk') {
      // Parse chunk if it's a JSON string
      const chunkData = typeof chunk === 'string' ? JSON.parse(chunk) : chunk
      const toolName = chunkData?.name || content?.name || '...'
      draft.pendingRequestActionMessage = 'Tool calling ' + toolName
    } else if (type === 'text') {
      draft.pendingRequestActionMessage = 'Preparing response.'
      if (isLastMessageOfTypeAi) {
        if (lastMessageDraft.id === response.id) {
          if (streaming) {
            lastMessageDraft.message += content.text
          }
          lastMessageDraft.type = MessageType.AiMsg
        } else {
          draft.messages.push({
            ...response,
            message: content.text,
            pass_to_human: pass_to_human ?? false,
            type: MessageType.AiMsg,
          })
        }
      } else {
        draft.messages.push({
          ...response,
          message: content.text,
          pass_to_human: pass_to_human ?? false,
          type: MessageType.AiMsg,
        })
      }
    } else if (type === 'interrupt') {
      draft.pendingRequestActionMessage = 'Require user input.'
      draft.messages.push({
        ...response,
        message: content,
        type: MessageType.InterruptMsg,
      })
    } else if (type === 'reasoning') {
      draft.pendingRequestActionMessage = 'Reasoning.'
      // Extract reasoning text from content or reasoning field
      const reasoningText = reasoning || content?.text || content
      if (isLastMessageOfTypeAi) {
        // Accumulate reasoning steps as an array
        if (!lastMessageDraft.reasoning) {
          lastMessageDraft.reasoning = []
        }
        if (Array.isArray(lastMessageDraft.reasoning)) {
          lastMessageDraft.reasoning.push(reasoningText)
        }
      } else {
        draft.messages.push({
          ...response,
          reasoning: [reasoningText],
          message: '',
          type: MessageType.AiMsg,
        })
      }
    } else if (type === 'tool_call') {
      // Parse chunk if it's a JSON string
      const toolCallData = typeof chunk === 'string' ? JSON.parse(chunk) : chunk

      // Skip tool response text blocks (they have type: "text" instead of name)
      // These are tool outputs, not tool calls
      if (toolCallData.type === 'text' && !toolCallData.name) {
        draft.pendingRequestActionMessage = 'Processing tool response.'
        return
      }

      draft.pendingRequestActionMessage = 'Tool calling ' + (toolCallData.name || '...')
      if (isLastMessageOfTypeAi) {
        // Initialize tool_calls array if needed
        if (!lastMessageDraft.tool_calls) {
          lastMessageDraft.tool_calls = []
        }
        if (Array.isArray(lastMessageDraft.tool_calls)) {
          // Find existing tool call by id or add new
          if (toolCallData.id) {
            const existingIndex = lastMessageDraft.tool_calls.findIndex(
              (tc: any) => tc.id === toolCallData.id
            )
            if (existingIndex >= 0) {
              // Merge: preserve name/args from earlier chunks
              lastMessageDraft.tool_calls[existingIndex] = {
                ...lastMessageDraft.tool_calls[existingIndex],
                ...toolCallData,
                name: toolCallData.name || lastMessageDraft.tool_calls[existingIndex].name,
              }
            } else {
              lastMessageDraft.tool_calls.push(toolCallData)
            }
          } else if (toolCallData.name) {
            // Has name but no id — add it
            lastMessageDraft.tool_calls.push(toolCallData)
          }
          // Skip entries with no id and no name (orphan delta chunks)
        }
      } else {
        draft.messages.push({
          id: response.id,
          reasoning: [],
          tool_calls: [toolCallData],
          message: '',
          type: MessageType.AiMsg,
        })
      }
    } else if (type === 'tool_result') {
      // Pair tool result with its tool call
      draft.pendingRequestActionMessage = 'Tool Responded.'
      const resultId = content?.tool_call_id
      const resultToolName = content?.tool_name
      if (isLastMessageOfTypeAi && lastMessageDraft.tool_calls) {
        lastMessageDraft.tool_calls = lastMessageDraft.tool_calls.map((toolCall: any) => {
          if (resultId && toolCall.id === resultId) {
            return { ...toolCall, result: content?.result, name: toolCall.name || resultToolName }
          }
          return toolCall
        })
        // Fallback: match by tool name if no ID match
        if (resultToolName && resultId) {
          const hasMatch = lastMessageDraft.tool_calls.some((tc: any) => tc.id === resultId)
          if (!hasMatch) {
            const idx = [...lastMessageDraft.tool_calls]
              .reverse()
              .findIndex((tc: any) => tc.name === resultToolName && !tc.result)
            if (idx >= 0) {
              const realIdx = lastMessageDraft.tool_calls.length - 1 - idx
              lastMessageDraft.tool_calls[realIdx] = {
                ...lastMessageDraft.tool_calls[realIdx],
                result: content?.result,
              }
            }
          }
        }
      }
    } else if (type === 'tool_response') {
      console.warn('tool_response response', response)
      draft.pendingRequestActionMessage = 'Tool Responded.'

      const updated_tool_calls = lastMessageDraft.tool_calls.map((toolCall) => {
        if (toolCall.id === chunk.tool_call_id) {
          toolCall.response = chunk.content
        }
        return toolCall
      })
      lastMessageDraft.tool_calls = updated_tool_calls
    } else if (type === 'workflow_saved') {
      draft.pendingRequestActionMessage = null
      draft.pendingAiResponse = false
      draft.streamingAiResponse = false
      draft.messages.push({
        ...response,
        message: content,
        type: MessageType.WorkflowSavedMsg,
      })
    } else if (type === 'workflow_test_start') {
      draft.pendingRequestActionMessage = 'Testing workflow...'
      draft.pendingAiResponse = true
      draft.streamingAiResponse = true
      draft.messages.push({
        ...response,
        message: content,
        type: MessageType.WorkflowTestMsg,
      })
    } else if (type === 'workflow_test_event') {
      draft.pendingRequestActionMessage = 'Running workflow test...'
      // Update the existing test message with latest event data
      const lastMsg = draft.messages[draft.messages.length - 1]
      if (lastMsg && lastMsg.type === MessageType.WorkflowTestMsg) {
        const msgData = lastMsg.message as any
        if (!msgData?.events) {
          lastMsg.message = { ...(typeof msgData === 'object' ? msgData : {}), events: [] } as any
        }
        (lastMsg.message as any).events.push(content)
      }
    } else if (type === 'workflow_test_complete') {
      draft.pendingRequestActionMessage = null
      draft.pendingAiResponse = false
      draft.streamingAiResponse = false
      // Update the test message with completion
      const lastTestMsg = draft.messages[draft.messages.length - 1]
      if (lastTestMsg && lastTestMsg.type === MessageType.WorkflowTestMsg) {
        const msgData = lastTestMsg.message as any
        lastTestMsg.message = {
          ...(typeof msgData === 'object' ? msgData : {}),
          ...content,
          completed: true,
        } as any
      }
    } else if (type === 'workflow_test_skipped' || type === 'workflow_test_error') {
      draft.pendingRequestActionMessage = null
      draft.pendingAiResponse = false
      draft.streamingAiResponse = false
      const lastSkipMsg = draft.messages[draft.messages.length - 1]
      if (lastSkipMsg && lastSkipMsg.type === MessageType.WorkflowTestMsg) {
        const msgData = lastSkipMsg.message as any
        lastSkipMsg.message = {
          ...(typeof msgData === 'object' ? msgData : {}),
          ...content,
          completed: true,
        } as any
      } else {
        draft.messages.push({
          ...response,
          message: content,
          type: MessageType.WorkflowTestMsg,
        })
      }
    } else {
      console.warn('unknown response', response)
    }
  })

const onUserSendMessage = (
  state: DeepAgentState,
  {
    message,
    model,
    thread_id,
  }: {
    message: string
    model?: string
    thread_id: string
    action_response: string
    feedback: string
  }
): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.pendingAiResponse = true
    draft.streamingAiResponse = false
    draft.pendingRequestActionMessage = 'Thinking...'
    draft.thread_id = thread_id
    draft.previousUserMessage = message
    // Remember the model chosen for this build so the save payload can reuse it.
    if (model) {
      draft.selectedModel = model
    }
    draft.messages.push({
      message,
      type: MessageType.UserMsg,
    })
  })

const onGetAgentResponseFailure = (state: DeepAgentState): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.pendingAiResponse = false
    draft.streamingAiResponse = false
    draft.pendingRequestActionMessage = null
  })

const onGetAgentResponseStreamCompleted = (state: DeepAgentState): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.pendingAiResponse = false
    draft.streamingAiResponse = false
    draft.pendingRequestActionMessage = null
    // Mark the last AI message as committed
    const lastMessage = draft.messages[draft.messages.length - 1]
    if (lastMessage && lastMessage.type === MessageType.AiMsg) {
      lastMessage.commited = true
    }
  })
const onSendFeedback = (
  state: DeepAgentState,
  { feedback, message }: { feedback: FeedbackType; message: Message }
): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.messages = draft.messages.map((msg) =>
      msg.id === message.id ? { ...msg, feedback } : msg
    )
  })

const onRedoMessage = (state: DeepAgentState, { message }: { message: Message }): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    for (let i = draft.messages.length - 1; i >= 0; i--) {
      const msg = draft.messages[i]
      if (msg && msg.type === MessageType.AiMsg && msg.id === message.id) {
        draft.messages.splice(i, 1)
        break
      }
    }
  })

const onClearMessages = (state: DeepAgentState): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.messages = []
    draft.pendingAiResponse = false
    draft.streamingAiResponse = false
    draft.pendingRequestActionMessage = null
    draft.previousUserMessage = null
    draft.agentId = null
    draft.selectedModel = null
    draft.buildMode = 'agent'
  })

const onAddInitialMessage = (
  state: DeepAgentState,
  { initialMessage }: { initialMessage: string }
): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.messages.push({ message: initialMessage, type: MessageType.AiMsg })
  })

const onSaveDeepAgent = (state: DeepAgentState): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.savingAgent = true
    draft.saveError = null
  })

const onSaveDeepAgentSuccess = (
  state: DeepAgentState,
  { agent }: { agent: DeepAgent }
): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.savingAgent = false
    draft.currentAgent = agent
    draft.agentId = agent.id
  })

const onSaveDeepAgentFailure = (
  state: DeepAgentState,
  { error }: { error: string }
): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.savingAgent = false
    draft.saveError = error
  })

const onSetCurrentAgent = (
  state: DeepAgentState,
  { agent }: { agent: DeepAgent | null }
): DeepAgentState =>
  produce(state, (draft: DeepAgentState) => {
    draft.currentAgent = agent
    draft.agentId = agent?.id ?? null
  })

export {
  onGetAgentResponseStream,
  onUserSendMessage,
  onGetAgentResponseFailure,
  onGetAgentResponseStreamCompleted,
  onSendFeedback,
  onRedoMessage,
  onClearMessages,
  onAddInitialMessage,
  onSaveDeepAgent,
  onSaveDeepAgentSuccess,
  onSaveDeepAgentFailure,
  onSetCurrentAgent,
}
